import * as React from "react";
import { isEmpty } from "lodash";
import { DatePicker } from "../../../../../../react-core/Datepicker/DatePicker";
import { RadiosEditor } from "../../../../../../react-core/Html/RadiosEditor";
import { SelectEditor } from "../../../../../../react-core/Html/SelectEditor";
import { addDays, addMonths } from "../../../../../../react-core/utils/date";
import { JsonDateParser } from "../../../../../../react-core/Utils/JsonDateParser";
import { WithdrawalMethod } from "../../../../../../Wrap/Constants/Enums/Domain/WithdrawalMethod";
import { ProcessType } from "../../../../../Shared/IST/Resource/Content";
import { PercentageOrMoney } from "../../../../../Shared/PercentageOrMoney/PercentageOrMoney";
import { IProcessComponentProps, ProcessComponent } from "../../../../../Shared/Process/Process/ProcessComponent";
import { ContinueUntil } from "../../NewBusinessWizard/Resource/Contents";
import {
    datePickerOptions,
    FrequencyArraysMap,
    FrequencyTypeArrays,
    WithdrawalMethodArray,
    WithdrawalMethodMap,
} from "../Resource/Contents";
import { calculateMoney } from "./InvestmentToWithdrawal";
import { InstrumentSelection } from "../../../../../../Wrap/Models/Models/InstrumentSelection/InstrumentSelection";
import { MoneyOrPercentageType } from "../../../../../../Wrap/Constants/Enums/MoneyOrPercentageType";
import { getISTPaymentAmount, getReRegTransferAmount } from "../../../../../Shared/IST/Components/Shared/Utils";
import { GiaBase } from "../../../../../../Wrap/Models/Models/QuoteAndApplyBase/GiaBase";
import { IsaBase } from "../../../../../../Wrap/Models/Models/QuoteAndApplyBase/IsaBase";
import { BankAccountIdType } from "../../../../../../Wrap/Constants/Enums/Domain/BankAccountIdType";
import { VerificationCheckStatus } from "../../../../../../Wrap/Constants/Enums/Domain/VerificationCheckStatus";
import { getQueryParameters } from "../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { CommonHelper } from "../../../../../../react-core/Helpers/CommonHelper";
import { TransferISTSelection } from "../../../../../../Wrap/Models/Models/InstrumentSelection/TransferISTSelection";
import { RegularWithdrawalModel } from "../../../../../../Wrap/Models/Models/Processes/RegularWithdrawal/RegularWithdrawalModel";
import { Frequency } from "../../../../../../Wrap/Constants/Enums/Domain/Frequency";
import { isNullOrEmpty } from "../../../../../../react-core/Utils/Arrays";

interface IWithdrawalProps extends IProcessComponentProps {
    type?: ProcessType;
    bankAccountInstruction?: Array<any>;
    allBankAccounts?: any;
    ownerId?: string;
    onBankChange?: (bank) => void;
    instruments?: Array<InstrumentSelection>;
    model: GiaBase | IsaBase;
    keyString: string;
    reRegInvestments: TransferISTSelection[];
    existingWithdrawal?: RegularWithdrawalModel;
}

interface IWithdrawalState {
    isAddingNewBank;
}

export class Withdrawal extends ProcessComponent<IWithdrawalProps, IWithdrawalState> {
    constructor(props: any) {
        super(props);
        this.state = {
            isAddingNewBank: false,
        };
    }

    componentDidMount() {
        super.componentDidMount();
        const { data, onChange, type } = this.props;
        const firstDate = data?.ClientRegularPayment?.FirstCollectionDate;
        const changes: any = {};
        if (!firstDate) {
            changes.FirstCollectionDate = JsonDateParser.toJsonDateNoOffset(addDays(addMonths(new Date(), 1), 1));
        }
        if (data?.ClientRegularPayment?.WithdrawalMethod === undefined) {
            changes.WithdrawalMethod = WithdrawalMethod.TransferToWrapCash;
        }

        if (!isEmpty(changes)) {
            this.onDataChange(onChange, changes, data, "ClientRegularPayment");
        }
        if (isNullOrEmpty(data?.ClientRegularPayment?.Withdrawal?.Frequency) && type == ProcessType.NB) {
            this.onDataChange(
                (changes) => this.onDataChange(onChange, changes, data, "ClientRegularPayment"),
                { Frequency: Frequency.Monthly },
                data.ClientRegularPayment,
                "Withdrawal"
            );
        }
    }

    private renderUntilDate(): JSX.Element {
        const { data, onChange } = this.props;
        return (
            <div className="col-12 col-sm-3">
                <DatePicker
                    formGroupCssWrapper="control-size-430"
                    label="Continue - until date"
                    selected={JsonDateParser.toDateNoOffset(data?.ClientRegularPayment?.DeferredFromDate)}
                    moreThan={{
                        errorMessage: "End date must be later than start date",
                        value: new Date(data?.ClientRegularPayment?.FirstCollectionDate),
                    }}
                    onChange={(v) =>
                        this.onDataChange(
                            onChange,
                            { DeferredFromDate: JsonDateParser.toJsonDateNoOffset(v) },
                            data,
                            "ClientRegularPayment"
                        )
                    }
                    alwaysValidateOnUpdate={false}
                    {...datePickerOptions}
                    required="Please select until date"
                />
            </div>
        );
    }

    public render(): JSX.Element {
        const { data, onChange, onRef, type, instruments, reRegInvestments } = this.props;
        return (
            <>
                <div className="mb-8">
                    <PercentageOrMoney
                        label={"Withdrawal amount"}
                        data={data?.ClientRegularPayment?.Withdrawal}
                        isReview={this.props.isReview}
                        onChange={(v) => {
                            delete v.Frequency;
                            if (data?.Allocations && data?.Allocations.length > 0) {
                                if (v?.Money?.Amount) {
                                    // need Change money
                                    const _data = calculateMoney(
                                        data?.Allocations,
                                        v?.Money,
                                        reRegInvestments,
                                        instruments,
                                        this.props.model,
                                        this.props.keyString
                                    );
                                    onChange({ Allocations: _data });
                                } else {
                                    // just change percentage
                                    data?.Allocations?.forEach((e) => {
                                        e.Percentage = v?.Percentage;
                                        e.Value = null;
                                    });
                                    onChange({ Allocations: data?.Allocations });
                                }
                            }
                            this.onDataChange(
                                (changes) => this.onDataChange(onChange, changes, data, "ClientRegularPayment"),
                                v,
                                data.ClientRegularPayment,
                                "Withdrawal"
                            );
                        }}
                        required={"Withdrawal amount is required"}
                        validationRules={[
                            {
                                errorMessage: "Withdrawal amount must be > 0 and cannot exceed Payments in amount.",
                                validator: (v) => {
                                    if (this.props.type == ProcessType.WhatIf) {return true;}
                                    if (data?.ClientRegularPayment?.Withdrawal?.Type == MoneyOrPercentageType.Money) {
                                        // Cal total payments in amount
                                        const totalAmount = getISTPaymentAmount(this.props.model, this.props.keyString);
                                        const reRegAmount = getReRegTransferAmount(
                                            this.props.model,
                                            this.props.keyString
                                        );
                                        return v > 0 && v <= totalAmount + reRegAmount;
                                    } else {
                                        return true;
                                    }
                                },
                            },
                            {
                                errorMessage: "Withdrawal percentage must be > 0",
                                validator: (v) => {
                                    if (
                                        data?.ClientRegularPayment?.Withdrawal?.Type == MoneyOrPercentageType.Percentage
                                    ) {
                                        return v > 0;
                                    } else {
                                        return true;
                                    }
                                },
                            },
                        ]}
                        onRef={(ref) => {
                            this.appendChild(ref);
                        }}
                        cssPercentageOrMoneyClasses="control-size-115"
                    />

                    <div className="row">
                        <div
                            className={!data?.ClientRegularPayment?.IsContinue ? "col-12 col-sm-3" : "col-12 col-sm-4"}
                        >
                            <SelectEditor
                                options={FrequencyTypeArrays}
                                getText={(v) => {
                                    return FrequencyArraysMap[v];
                                }}
                                required={"Withdrawal frequency is required"}
                                selected={data?.ClientRegularPayment?.Withdrawal?.Frequency}
                                label="Withdrawal frequency"
                                placeholder="Please select"
                                onChange={(v) => {
                                    this.onDataChange(
                                        (changes) => this.onDataChange(onChange, changes, data, "ClientRegularPayment"),
                                        { Frequency: v },
                                        data.ClientRegularPayment,
                                        "Withdrawal"
                                    );
                                }}
                            />
                        </div>
                        <div
                            className={!data?.ClientRegularPayment?.IsContinue ? "col-12 col-sm-3" : "col-12 col-sm-4"}
                        >
                            <DatePicker
                                label={
                                    this.props.type == ProcessType.WhatIf && this.props.existingWithdrawal
                                        ? "Next withdrawal date"
                                        : "First withdrawal date"
                                }
                                selected={JsonDateParser.toDateNoOffset(
                                    data?.ClientRegularPayment?.FirstCollectionDate
                                )}
                                moreThan={{
                                    value: addMonths(new Date(), 1),
                                    errorMessage: `'${
                                        this.props.type == ProcessType.WhatIf && this.props.existingWithdrawal
                                            ? "Next"
                                            : "First"
                                    } withdrawal date' cannot be any date < One month in the future of today's date`,
                                }}
                                showYearDropdown={true}
                                filterDate={{
                                    errorMessage: "Date must also be between 1-28 of any month",
                                    validator: (v) => {
                                        if (v?.getDate() > 28) {return false;}
                                        return true;
                                    },
                                }}
                                onChange={(d) =>
                                    this.onDataChange(
                                        onChange,
                                        { FirstCollectionDate: JsonDateParser.toJsonDateNoOffset(d) },
                                        data,
                                        "ClientRegularPayment"
                                    )
                                }
                                required={`${
                                    this.props.type == ProcessType.WhatIf &&
                                    data?.ClientRegularPayment?.DeferredFromDate
                                        ? "Next"
                                        : "First"
                                } withdrawal date is required`}
                                {...datePickerOptions}
                            />
                        </div>

                        <div
                            className={!data?.ClientRegularPayment?.IsContinue ? "col-12 col-sm-3" : "col-12 col-sm-4"}
                        >
                            <SelectEditor
                                cssWrapper="control-size-430"
                                options={ContinueUntil}
                                getText={(v) => {
                                    return v;
                                }}
                                required="Please select"
                                selected={data?.ClientRegularPayment?.IsContinue ? ContinueUntil[0] : ContinueUntil[1]}
                                label="Continue until"
                                onChange={(v) =>
                                    v === ContinueUntil[0]
                                        ? this.onDataChange(
                                              onChange,
                                              { IsContinue: true, DeferredFromDate: null },
                                              data,
                                              "ClientRegularPayment"
                                          )
                                        : this.onDataChange(
                                              onChange,
                                              { IsContinue: false, DeferredFromDate: null },
                                              data,
                                              "ClientRegularPayment"
                                          )
                                }
                            />
                        </div>
                        {!data?.ClientRegularPayment?.IsContinue && this.renderUntilDate()}
                    </div>

                    {type === ProcessType.NB && (
                        <>
                            <RadiosEditor
                                cssWrapper="rg-inline"
                                options={
                                    this.props.allBankAccounts &&
                                    !CommonHelper.isNullOrEmpty(getQueryParameters()["hierarchyid"]) &&
                                    this.props.allBankAccounts.filter(
                                        (ele) => ele.VerificationStatus == VerificationCheckStatus.Passed
                                    ).length > 0
                                        ? WithdrawalMethodArray
                                        : [WithdrawalMethod.TransferToWrapCash]
                                }
                                getText={(v) => WithdrawalMethodMap[v]}
                                label="Method"
                                infoText="Withdrawals can only be paid to external bank accounts once they have been verified."
                                selected={data?.ClientRegularPayment?.WithdrawalMethod}
                                onChange={(v) => {
                                    this.onDataChange(onChange, { WithdrawalMethod: v }, data, "ClientRegularPayment");
                                }}
                            />

                            {data?.ClientRegularPayment?.WithdrawalMethod == WithdrawalMethod.BankAccount && (
                                <SelectEditor
                                    options={this.props.allBankAccounts.filter(
                                        (ele) => ele.VerificationStatus == VerificationCheckStatus.Passed
                                    )}
                                    getText={(o) => o.AccountNumber}
                                    selected={this.props.allBankAccounts.find(
                                        (ele) => ele.BankAccountId == data?.ClientRegularPayment?.BankAccountId
                                    )}
                                    required={"Please select"}
                                    cssWrapper="control-size-430"
                                    placeholder="Please select"
                                    onChange={(v) => {
                                        this.onDataChange(
                                            onChange,
                                            {
                                                BankAccountId: v.BankAccountId,
                                                BankAccountIdType: BankAccountIdType.BankAccount,
                                            },
                                            data,
                                            "ClientRegularPayment"
                                        );
                                    }}
                                />
                            )}
                        </>
                    )}
                </div>
            </>
        );
    }
}
